const creativeProfiles = new Set(["creative_brief", "script_writing", "script_review", "media_production", "media_review"]);
const workflowFilterDefinitions = [
  {value: "creative_brief", label: "需求", description: "创意需求与结构化简报"},
  {value: "script_writing", label: "脚本", description: "自由脚本、口播脚本、分镜脚本与版本"},
  {value: "script_review", label: "脚本审核", description: "脚本审核意见与审批决定"},
  {value: "media_production", label: "制作", description: "制作依据、附件、素材版本与分发"},
  {value: "media_review", label: "素材审阅", description: "指定素材版本的批注、对比与审批"},
];
const workflowDefinitionByType = new Map(workflowFilterDefinitions.map((definition) => [definition.value, definition]));

export function creativeTaskProfileFor(project = {}, task = {}) {
  const profile = (Array.isArray(project?.creativeTaskProfiles) ? project.creativeTaskProfiles : []).find((item) => String(item?.taskTypeKey ?? "") === String(task?.TaskTypeKey ?? ""));
  return profile && creativeProfiles.has(profile.profileType) ? profile : null;
}

export function creativeTaskWorkType(project = {}, task = {}) {
  return creativeTaskProfileFor(project, task)?.profileType || "";
}

export function creativeWorkTypeLabel(value) {
  return workflowDefinitionByType.get(String(value || ""))?.label || "";
}

export function creativeTaskBadgeLabel(project = {}, task = {}) {
  const workType = creativeTaskWorkType(project, task);
  if (!workType) return "";
  const stage = String(task?.creativeContext?.stage || "");
  const conciseLabel = ({
    "creative_brief:brief": "需求配置中",
    "script_writing:script": "脚本创作中",
    "script_review:script_review": "脚本待审核",
    "media_production:production": "制作中",
    "media_review:review": "素材审阅中",
  })[`${workType}:${stage}`];
  if (conciseLabel) return conciseLabel;
  const progress = ({
    brief: "配置中", brief_ready: "已移交", script: "创作中", script_review: "待审核", script_approved: "已批准",
    production: "制作中", asset: "素材已登记", review: "审阅中", approved: "已批准", changes_requested: "需修改",
    rejected: "已拒绝", distribution: "待分发",
  })[stage] || "";
  return `${creativeWorkTypeLabel(workType)}${progress ? ` · ${progress}` : ""}`;
}

export function creativeWorkflowFilterOptions(profileRecords = [], taskTypes = []) {
  const profiles = new Map();
  for (const item of Array.isArray(profileRecords) ? profileRecords : []) {
    const profileType = typeof item === "string" ? item : item?.profileType;
    if (creativeProfiles.has(profileType) && !profiles.has(profileType)) profiles.set(profileType, item);
  }
  if (!profiles.size) return [];
  const taskTypeNames = new Map((Array.isArray(taskTypes) ? taskTypes : []).filter((item) => item && item.isDeleted !== true).map((item) => [String(item.key), String(item.name || item.value || item.key)]));
  const configured = workflowFilterDefinitions.filter((definition) => profiles.has(definition.value)).map((definition) => {
    const profile = profiles.get(definition.value);
    const taskTypeKey = typeof profile === "object" ? String(profile.taskTypeKey ?? "") : "";
    const nativeTypeName = typeof profile === "object" ? taskTypeNames.get(String(profile.taskTypeKey ?? "")) || String(profile.taskTypeName || "") : "";
    return {...definition, taskTypeKey, nativeTypeName, description: definition.description};
  });
  return [
    {value: "", label: "所有任务", description: "显示项目中的全部任务", nativeTypeName: ""},
    {value: "enabled", label: "创意流程", description: "只显示需求、脚本、制作和审阅任务", nativeTypeName: ""},
    ...configured,
  ];
}

export function creativeTaskCreationPolicy(profileRecords = [], taskTypes = [], workType = "") {
  const selectedWorkType = String(workType || "");
  if (!selectedWorkType || selectedWorkType === "enabled") {
    return {allowed: true, taskTypeKey: "", mode: "generic", reason: ""};
  }
  const profile = (Array.isArray(profileRecords) ? profileRecords : []).find((item) => {
    const profileType = typeof item === "string" ? item : item?.profileType;
    return profileType === selectedWorkType;
  });
  const taskTypeKey = typeof profile === "object" ? String(profile?.taskTypeKey ?? "") : "";
  const taskTypeExists = (Array.isArray(taskTypes) ? taskTypes : []).some((item) => item?.isDeleted !== true && String(item?.key ?? "") === taskTypeKey);
  if (!profile || !taskTypeKey || !taskTypeExists) {
    return {allowed: false, taskTypeKey: "", mode: "unavailable", reason: "当前工作流程尚未绑定可用的任务类型"};
  }
  if (selectedWorkType === "script_review" || selectedWorkType === "media_review") {
    return {allowed: false, taskTypeKey, mode: "automatic", reason: selectedWorkType === "script_review" ? "脚本审核任务由脚本提交后自动生成" : "素材审阅任务由素材提交后自动生成"};
  }
  return {allowed: true, taskTypeKey, mode: "mapped", reason: ""};
}

export function enabledCreativeProjectViewKeys(profileTypes = [], performanceEnabled = false) {
  const profiles = new Set(profileTypes.filter((profile) => creativeProfiles.has(profile)));
  return new Set([
    ...(profiles.size ? ["CreativeAnalytics"] : []),
    ...(profiles.size ? ["Timeline"] : []),
    ...(profiles.has("script_writing") ? ["CreativeScripts"] : []),
    ...(profiles.has("media_production") || profiles.has("media_review") ? ["CreativeReviews", "CreativeAssets"] : []),
    ...(profiles.has("media_production") ? ["CreativeEdits"] : []),
    ...(profiles.has("script_review") || profiles.has("media_review") ? ["CreativeWorkflows"] : []),
    ...(profiles.has("media_production") ? ["CreativeDistributions"] : []),
    // The data workspace is also where an administrator connects the first
    // advertising account. Hiding it until facts exist creates a dead end.
    ...(profiles.size || performanceEnabled ? ["CreativePerformance"] : []),
  ]);
}

export function enabledCreativeBaseViews(viewKeys) {
  const baseByKey = {CreativeScripts:"scripts",CreativeReviews:"reviews",Timeline:"timeline",CreativeAssets:"assets",CreativeWorkflows:"workflows",CreativeDistributions:"distributions"};
  return new Set([...viewKeys].map((key) => baseByKey[key]).filter(Boolean));
}

export function partitionCreativeProjectViews(views = []) {
  const available = Array.isArray(views) ? views.filter(Boolean) : [];
  const creativeProject = available.some((view) => view.isSystemView === true);
  if (!creativeProject) return {primary: available, secondary: [], creativeProject: false};
  // A creative project used to expose every domain table as an equal top-level
  // tab. Keep the stable project anchors in the header; the visible workflow
  // navigator owns stage-to-stage movement and the remaining views stay
  // available as project tools.
  // The workflow navigator already owns ProjectListView as “需求与任务”.
  // Repeating it in the project header under another label creates two
  // competing entrances to the same work. Keep only the overview anchor here.
  const headerAnchors = new Set(["CreativeAnalytics"]);
  const primary = available.filter((view) => headerAnchors.has(String(view.keyName || "")));
  const primaryKeys = new Set(primary.map((view) => String(view.keyName || view._id || "")));
  return {
    primary,
    secondary: available.filter((view) => !primaryKeys.has(String(view.keyName || view._id || ""))),
    creativeProject: true,
  };
}
